import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { isRankingType } from '@flipster/apis/v2/trading-competition/types';
import { FormattedMessage } from '@flipster/i18n';
import { css } from '@flipster/styled-system/css';
import { stack, vstack } from '@flipster/styled-system/patterns';

import { MaxWidthLayout } from '../component/layout';
import { competitionBunja, competitionEventsBunja, competitionTabsBunja } from '../state';
import { DailyCheckInContent } from './daily-check-in';
import { EventTab } from './event-tab';
import { competitionRankingsApiBunja } from './leaderboard/state';
import { LuckyDraw } from './lucky-draw';
import { PricePredictionContent } from './price-prediction';
import { RollAndMove } from './roll-and-move';
import { VolumePnl } from './volume-pnl';
import { XMasEtcContent } from './x-mas-etc';

const TabContent = () => {
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);
  const { initializePagination } = useBunja(competitionRankingsApiBunja);
  // 탭이 바뀔 때 리더보드 관련 클라이언트 상태 초기화
  useEffect(() => {
    initializePagination();
  }, [selectedEventTabValue, initializePagination]);

  if (isRankingType(selectedEventTabValue)) return <VolumePnl />;
  if (selectedEventTabValue === 'lucky-draw') return <LuckyDraw />;
  if (selectedEventTabValue === 'roll-and-dice') return <RollAndMove />;
  if (selectedEventTabValue === 'daily-check-in') return <DailyCheckInContent />;
  if (selectedEventTabValue === 'price-prediction') return <PricePredictionContent />;
  if (selectedEventTabValue === 'x-mas-etc') return <XMasEtcContent />;

  return null;
};

export const Content = () => {
  const { competitionContentful } = useBunja(competitionBunja);
  const { availableEventCountAtom } = useBunja(competitionEventsBunja);
  const { eventTabs } = useBunja(competitionTabsBunja);

  const availableEventCount = useAtomValue(availableEventCountAtom);
  const hasMultipleTabs = eventTabs && eventTabs.length > 1;

  if (!competitionContentful) return null;

  return (
    <MaxWidthLayout css={{ pos: 'relative', mb: 64 }}>
      <div className={vstack({ my: 64, mb: { tabletDown: 32 } })}>
        {hasMultipleTabs && (
          <p
            className={css({
              textStyle: 'Title/S',
              color: 'content.default.level-5',
            })}
          >
            <FormattedMessage id='Competition.r251211.Value_events_available' values={{ value: availableEventCount }} />
          </p>
        )}
        <p
          className={css({
            textStyle: 'Display/XL',
            color: 'content.default.level-5',
            textAlign: 'center',
            pt: 16,
            textWrap: 'balance',
          })}
        >
          {competitionContentful.eventsTitle}
        </p>
      </div>
      <section
        id='competition-content'
        className={vstack({
          gap: { tablet: 16 },
          mt: { base: 32, tablet: 64 },
          pb: { base: 24, tablet: 40 },
          rounded: { base: 12, tablet: 28 },
          border: '1px solid transparent',
        })}
        style={{
          background: `linear-gradient(0deg, #111111, #111111), linear-gradient(180deg, var(--key-color) 0%, #2A2A2A 40%, #2A2A2A 100%)`,
          backgroundClip: 'padding-box, border-box',
          backgroundOrigin: 'border-box',
        }}
      >
        <EventTab />
        <div className={stack({ w: '100%', px: { base: 16, tablet: 40 }, mt: { tabletDown: 16 } })}>
          <TabContent />
        </div>
      </section>
      <div
        role='separator'
        style={{
          background: `radial-gradient(60% 60%, color-mix(in srgb, var(--key-color) 80%, transparent) 0%, color-mix(in srgb, var(--key-color) 35%, transparent) 45%, color-mix(in srgb, var(--key-color) 12%, transparent) 70%, transparent 100%)`,
        }}
        className={css({
          pos: 'absolute',
          left: '50%',
          top: 40,
          transform: 'translateX(-50%)',
          w: { base: '100%', tablet: 426 },
          aspectRatio: '1 / 1',
          rounded: '50%',
          filter: 'blur(20px)',
          opacity: 0.7,
          pointerEvents: 'none',
          zIndex: -1,
        })}
      />
    </MaxWidthLayout>
  );
};
